import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

const getHiringEventApiUrl = (workerId: string, hiringEventId: string) => {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/hiring-events/${hiringEventId}`;
};

function getHiringEventQueryKey(workerId: string, hiringEventId: string) {
  return [getHiringEventApiUrl(workerId, hiringEventId)];
}

const hiringEventAttributesSchema = z.object({
  address: z.object({
    formatted: z.string(),
  }),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
  addressDescription: z.string().optional(),
  distance: z.number().optional(),
  fixedSchedule: z
    .object({
      startDate: z.string(),
      endDate: z.string(),
      interviewLength: z.number(),
      availableInterviewers: z.number(),
      overfillFirstSlots: z.boolean().optional(),
    })
    .optional(),
});
export type HiringEvent = z.infer<typeof hiringEventAttributesSchema> & { id: string };

const hiringEventResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(API_TYPES.hiringEvent),
    attributes: hiringEventAttributesSchema,
    relationships: z.object({
      workplaces: z.object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.workplace),
            id: z.string(),
          })
        ),
      }),
    }),
  }),
});
export type HiringEventResponse = z.infer<typeof hiringEventResponseSchema>;

function transformResponseIntoHiringEvent(response: HiringEventResponse): HiringEvent {
  const { data } = response;
  return { ...data.attributes, id: data.id };
}

interface UseGetHiringEventParams {
  workerId: string;
  hiringEventId: string;
}

export function useGetHiringEvent(
  params: UseGetHiringEventParams,
  options?: UseQueryOptions<HiringEvent>
) {
  const { workerId, hiringEventId } = params;

  return useQuery({
    queryKey: getHiringEventQueryKey(workerId, hiringEventId),
    queryFn: async () => {
      const response = await get({
        url: getHiringEventApiUrl(workerId, hiringEventId),
        responseSchema: hiringEventResponseSchema,
      });
      const { success, data } = hiringEventResponseSchema.safeParse(response.data);
      return transformResponseIntoHiringEvent(success ? data : response.data);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_PLACEMENT_CANDIDATE_FAILURE,
    },
    ...options,
  });
}
